import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const ArtistCard = ({ artist }) => (
  <div className="glass-card p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300" data-testid={`artist-card-${artist.name.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="relative">
      {artist.image ? (
        <div className="w-20 h-20 rounded-2xl overflow-hidden mb-4 group-hover:scale-110 transition-transform border border-white/10">
          <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#E8720C]/20 to-[#E8720C]/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <span className="text-2xl font-black text-[#E8720C]">{artist.name.charAt(0)}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-lg font-bold">{artist.name}</h3>
        <CheckCircle className="w-4 h-4 text-[#1DB954]" />
      </div>
      <p className="text-sm text-white/40 mb-1">{artist.genre}</p>
      {artist.tag && <p className="text-[10px] text-[#E8720C]/70 font-medium mb-3">{artist.tag}</p>}
      <div className="flex items-center gap-2">
        {artist.spotify_url && (
          <a href={artist.spotify_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-black font-semibold text-xs transition-all hover:scale-105"
            data-testid={`spotify-btn-${artist.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <SpotifyIcon className="w-4 h-4" /> Spotify
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        )}
        {artist.youtube_url && (
          <a href={artist.youtube_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#FF0000]/10 hover:bg-[#FF0000]/20 text-[#FF0000] font-semibold text-xs transition-all hover:scale-105 border border-[#FF0000]/20"
            data-testid={`youtube-btn-${artist.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <YouTubeIcon className="w-4 h-4" /> YouTube
          </a>
        )}
      </div>
    </div>
  </div>
);

const FeaturedArtists = ({ compact = false }) => {
  const { t } = useLanguage();
  const fa = (key) => t(`featuredArtists.${key}`);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const url = compact
          ? `${API_URL}/api/artists/?featured=true&limit=6`
          : `${API_URL}/api/artists/?featured=true`;
        const res = await axios.get(url);
        setArtists(res.data);
      } catch (err) {
        console.error('Error fetching artists:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, [compact]);

  const displayArtists = compact ? artists.slice(0, 3) : artists;

  if (loading) return null;
  if (artists.length === 0) return null;

  return (
    <section className="section-padding relative" data-testid="featured-artists-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#E8720C]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container-custom relative">
        <div className="text-center mb-4">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">
            {fa('overline')}
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            {fa('title1')}{' '}
            <span className="gold-text">{fa('title2')}</span>
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
            {fa('description')}
          </p>
        </div>

        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#E8720C]/40" />
          <p className="text-sm text-[#E8720C]/70 font-medium italic">{fa('trustLine')}</p>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#E8720C]/40" />
        </div>

        <div className={`grid gap-6 ${compact ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {displayArtists.map((artist) => (
            <ArtistCard key={artist.id || artist.name} artist={artist} />
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-white/40 mb-6">{fa('bottomText')}</p>
          {compact && (
            <Link to="/artists" className="inline-flex items-center gap-2 btn-secondary text-sm" data-testid="view-all-artists-btn">
              {fa('viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export { ArtistCard, SpotifyIcon, YouTubeIcon };
export default FeaturedArtists;
